/**
 * RECEPTION-OS-BUILD-V1 -- the consent-form PRINT page
 * (Q-03 + Q-DP-PRINT = B: the desk prints bills, receipts, AND consent
 * forms; R-CONSENT: print-for-paper-signature with signed-copy upload).
 *
 * Route: /dashboard/patients/consent-print -- gated by the SAME
 * RequirePatientMaster guard as its sibling patient pages (the audience
 * that hosts the register wizard). Linked from the wizard's consent step
 * (paper arm), so the door is reachable without typing a URL (NAV law).
 *
 * CONTENT IS SOURCED-PENDING: the org's template rides
 * `registration.consent_form_template` (THE ONE settings reader; asset-
 * backed -- text lines and/or a hosted image). With no configured template
 * this page renders the honest placeholder -- legal text is NEVER invented
 * (the package's consent-mechanism law). The signed-copy upload half of
 * R-CONSENT already ships (consent-photo.tsx: the paper path's PENDING
 * PHOTO state); this page is the missing PRINT half.
 *
 * Zero PHI by construction: the sheet renders the org's own template only
 * -- no patient fields ride this page (the patient signs the PAPER).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchRegistrationConfig } from '@/dashboard/api';
import PrintSheet from '@/dashboard/ui/PrintSheet';
import {
  FALLBACK_REGISTRATION_CONFIG,
  type RegistrationConsentFormTemplate,
} from '@/dashboard/patients/register-wizard-model';

export const CONSENT_PRINT_TITLE = 'Consent form';
export const CONSENT_PRINT_PENDING_TITLE = 'Consent form templates pending';
export const CONSENT_PRINT_PENDING_BODY =
  'This organization has not provided its consent form yet. The tenant form ' +
  'is being sourced; once it is configured, this page prints it. Nothing is ' +
  'invented in the meantime.';
export const CONSENT_PRINT_BACK_LABEL = 'Back to registration';

export function ConsentFormPrintContent({
  template,
}: {
  readonly template: RegistrationConsentFormTemplate | null;
}): JSX.Element {
  if (template === null) {
    return (
      <div className="dashboard-page__note" data-testid="consent-print-pending">
        <h2>{CONSENT_PRINT_PENDING_TITLE}</h2>
        <p>{CONSENT_PRINT_PENDING_BODY}</p>
      </div>
    );
  }
  return (
    <div data-testid="consent-print-template">
      <h2>{template.title}</h2>
      {template.bodyLines.map((line, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={i}>{line}</p>
      ))}
      {template.imageUrl !== null ? (
        <img
          src={template.imageUrl}
          alt={template.title}
          className="consent-print__image"
        />
      ) : null}
    </div>
  );
}

export default function ConsentFormPrintPage(): JSX.Element {
  const navigate = useNavigate();
  const [template, setTemplate] =
    useState<RegistrationConsentFormTemplate | null>(
      FALLBACK_REGISTRATION_CONFIG.consentFormTemplate,
    );

  useEffect(() => {
    let cancelled = false;
    fetchRegistrationConfig()
      .then((c) => {
        if (!cancelled) setTemplate(c.consentFormTemplate);
      })
      .catch(() => {
        /* the placeholder stands -- a config fault never invents a form */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PrintSheet
      title={CONSENT_PRINT_TITLE}
      backLabel={CONSENT_PRINT_BACK_LABEL}
      onBack={() => navigate(-1)}
      testId="consent-print"
    >
      <ConsentFormPrintContent template={template} />
    </PrintSheet>
  );
}
